import { useEffect, useMemo, useState } from 'react';

import {
  useArchiveTemplate,
  useCreateTemplate,
  useCreateTemplateVersion,
  useEmailTemplates,
  useTemplateVersion,
  type EmailTemplateDto,
  type EmailTemplateVersionFullDto,
} from '../../api/email-templates';
import { Badge, Button, EmptyState, ErrorState, Field, Input, Modal, Select, Spinner, Textarea } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { formatWhen } from './lists-model';
import {
  CHANNEL_OPTIONS,
  EMPTY_DRAFT,
  SAMPLE_GREETING_NAME,
  buildTemplateCreate,
  buildVersionCreate,
  channelLabel,
  channelOf,
  isPlainTextChannel,
  errorLineOf,
  formatVersionLine,
  formatVersionsSummary,
  isArchived,
  latestVersionOf,
  renderPreview,
  scanTemplateVariables,
  sortTemplates,
  templateStatusLabel,
  variableChips,
  type PreviewTrack,
  type TemplateDraft,
  isKnownVariable,
  describeVariable,
} from './templates-model';

/**
 * «Шаблоны» — the THIRD tab of «Рассылки» (`?tab=templates`, T.Z134) over the
 * already-shipped templates surface (#33, T.Z130). The owner's words: «это
 * dropdown, а не редактор» — templates are to be created, corrected and retired
 * INSIDE the system, so this tab carries the whole lifecycle:
 *
 *   * the LIST (#33.1) — key, title, every version (number, date, the note),
 *     live first and archived sunk to the bottom with its badge;
 *   * the VIEW (#33.3) — a version's subject and its body in the SAME sandboxed
 *     iframe the letter previews use (`sandbox=""`, no scripts, no same-origin),
 *     rendered through the sample variables, plus the scanned inventory — or,
 *     since T.Z184, the version's RAW stored HTML, substitutions untouched;
 *   * the EDITOR — subject + the hand-crafted HTML in a monospace textarea, the
 *     changelog note, the live `{{var}}` scan and a per-track preview. Saving is
 *     ALWAYS a create (#33.2 for a new template's v1, #33.4 for the next
 *     version) — a version is immutable, there is no rewrite;
 *   * the ARCHIVE toggle (#33.5) through its confirm — no delete exists.
 *
 * The editor is deliberately NOT a WYSIWYG: letter HTML is hand-crafted (the
 * seeded letters are), and the preview iframe is the only «как оно будет
 * выглядеть» that does not lie.
 */

const TAB_HINT =
  'Письмо собирается из шаблона: тема + HTML с переменными {{name}} (имя получателя), {{start_url}} ' +
  '(ссылка на страницу курса — ИИ или Python, разрешается автоматически по программе ребёнка) и ' +
  '{{gosuslugi_test_link}} (страница курса на Госуслугах — заполняется в письмах «Ошибка подписи ' +
  'договора: не указано согласие родителей», «Пройдите вступительное испытание» и в рассылках списками, ' +
  'чей шаблон использует переменную). Правки всегда ' +
  'создают НОВУЮ версию — созданные версии неизменяемы, поэтому список, который уже отправлял письмо, читает ' +
  'ровно то, что отправлял.';

export function TemplatesSection() {
  const templates = useEmailTemplates();
  const [creating, setCreating] = useState(false);
  /** The template the «Новая версия» editor is open for (null = closed). */
  const [editorTemplate, setEditorTemplate] = useState<EmailTemplateDto | null>(null);
  /** The version the view modal shows (its body arrives through #33.3). */
  const [viewing, setViewing] = useState<{ template: EmailTemplateDto; version: number } | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<EmailTemplateDto | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const rows = useMemo(() => sortTemplates(templates.data?.data.templates ?? []), [templates.data]);

  return (
    <div className="mailings-templates" data-testid="mailings-templates">
      <section className="card">
        <div className="mailings-filters">
          <h2>Шаблоны писем</h2>
          <p className="mailings-lists-hint">{TAB_HINT}</p>
          <Button
            variant="primary"
            onClick={() => {
              setCreating((prev) => !prev);
              setEditorTemplate(null);
            }}
            data-testid="mailings-templates-create-toggle"
          >
            {creating ? 'Свернуть' : 'Новый шаблон'}
          </Button>
        </div>

        {notice ? (
          <p className="mailings-template-notice" data-testid="mailings-templates-notice">
            {notice}
          </p>
        ) : null}

        {creating ? (
          <div className="mailings-template-editor-wrap" data-testid="mailings-template-create">
            <TemplateEditor
              template={null}
              onCancel={() => setCreating(false)}
              onSaved={(created) => {
                setCreating(false);
                setNotice(`Шаблон «${created.title}» создан — версия 1 готова, его уже можно выбрать в списке рассылок.`);
              }}
            />
          </div>
        ) : null}

        {editorTemplate ? (
          <div className="mailings-template-editor-wrap" data-testid="mailings-template-version-editor">
            {/* Keyed by the template: switching the editor onto another row starts
                a fresh draft instead of mixing two corrections. */}
            <TemplateEditor
              key={editorTemplate.id}
              template={editorTemplate}
              onCancel={() => setEditorTemplate(null)}
              onSaved={(created) => {
                setEditorTemplate(null);
                setNotice(
                  `Создана версия ${created.version} шаблона «${created.title}» — прежние версии остались как были.`,
                );
              }}
            />
          </div>
        ) : null}
      </section>

      <section className="card card--flush" data-testid="mailings-templates-table-card">
        {templates.isPending ? (
          <Spinner label="Загружаем шаблоны" />
        ) : templates.isError ? (
          <ErrorState message={String(templates.error)} onRetry={() => void templates.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Пока нет ни одного шаблона"
            hint="Создайте шаблон: ключ, название, тема и HTML письма с переменными."
          />
        ) : (
          <TableWrap>
            <table className="data mailings-table" data-testid="mailings-templates-table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Ключ</th>
                  <th>Версии</th>
                  <th>Создан</th>
                  <th>Статус</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((template) => (
                  <TemplateRow
                    key={template.id}
                    template={template}
                    busy={templates.isFetching}
                    onView={(version) => setViewing({ template, version })}
                    onEdit={() => {
                      setEditorTemplate(template);
                      setCreating(false);
                    }}
                    onArchive={() => setArchiveTarget(template)}
                  />
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </section>

      <TemplateVersionModal
        open={viewing !== null}
        templateId={viewing?.template.id ?? null}
        version={viewing?.version ?? null}
        title={viewing?.template.title ?? ''}
        onClose={() => setViewing(null)}
      />

      <ArchiveTemplateModal
        template={archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onSettled={(archived, errorLine) => {
          setArchiveTarget(null);
          setNotice(errorLine ?? (archived ? ARCHIVED_NOTICE : RESTORED_NOTICE));
        }}
      />
    </div>
  );
}

const ARCHIVED_NOTICE =
  'Шаблон в архиве: он скрыт из выбора шаблонов и закрыт для новых версий. Списки, которые уже закрепили его версии, работают как работали.';
const RESTORED_NOTICE = 'Шаблон снова активен — его можно выбирать в списках рассылок и дополнять версиями.';

// ------------------------------------------------------------------ the row --

function TemplateRow({
  template,
  busy,
  onView,
  onEdit,
  onArchive,
}: {
  template: EmailTemplateDto;
  busy: boolean;
  onView: (version: number) => void;
  onEdit: () => void;
  onArchive: () => void;
}) {
  const archived = isArchived(template);
  const status = templateStatusLabel(template);
  const latest = latestVersionOf(template);
  return (
    <tr data-testid="mailings-template-row" className={archived ? 'mailings-template-row--archived' : undefined}>
      <td className="cell-primary">
        {template.title}{' '}
        <Badge tone={channelOf(template) === 'SMS' ? 'brand' : 'neutral'} testId="mailings-template-channel">
          {channelLabel(channelOf(template))}
        </Badge>
        <span className="mailings-template-subject" title={latest?.subject ?? undefined}>
          {latest ? latest.subject : '—'}
        </span>
      </td>
      <td className="cell-muted">
        <code className="mailings-template-key">{template.key}</code>
      </td>
      <td>
        <ul className="mailings-template-versions" data-testid="mailings-template-versions">
          {template.versions.map((version) => (
            <li key={version.version}>
              <button
                type="button"
                className="mailings-template-version-link"
                onClick={() => onView(version.version)}
                data-testid="mailings-template-version"
              >
                {formatVersionLine(version)}
              </button>
            </li>
          ))}
        </ul>
        <span className="cell-muted">{formatVersionsSummary(template)}</span>
      </td>
      <td className="cell-compact cell-muted">{formatWhen(template.createdAt)}</td>
      <td className="cell-compact">
        <Badge tone={status.tone} testId="mailings-template-status">
          {status.label}
        </Badge>
      </td>
      <td className="cell-compact">
        <span className="mailings-template-actions">
          <Button size="sm" disabled={busy} onClick={() => latest && onView(latest.version)} data-testid="mailings-template-open">
            Открыть
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={archived || busy}
            title={archived ? 'Шаблон в архиве — новые версии запрещены. Верните его из архива.' : undefined}
            onClick={onEdit}
            data-testid="mailings-template-new-version"
          >
            Новая версия
          </Button>
          <Button size="sm" disabled={busy} onClick={onArchive} data-testid="mailings-template-archive-toggle">
            {archived ? 'Из архива' : 'В архив'}
          </Button>
        </span>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------- the editor --

function TemplateEditor({
  template,
  onCancel,
  onSaved,
}: {
  /** null = a NEW template (key + title + v1); otherwise the next version of THIS one. */
  template: EmailTemplateDto | null;
  onCancel: () => void;
  /** Answers what the save created — a template (its v1) or the new version. */
  onSaved: (created: { title: string; version: number }) => void;
}) {
  const isCreate = template === null;
  const [draft, setDraft] = useState<TemplateDraft>(EMPTY_DRAFT);
  const [track, setTrack] = useState<PreviewTrack>('AI');
  const [localError, setLocalError] = useState<string | null>(null);

  const createTemplate = useCreateTemplate();
  const createVersion = useCreateTemplateVersion();

  // A correction starts from the LATEST version — the natural base for «правим
  // формулировку». The read is #33.3 (versions are immutable, so the cached
  // body is trustworthy); a failed read leaves the editor empty, with a warning.
  const latest = template ? latestVersionOf(template) : null;
  const base = useTemplateVersion(template?.id ?? null, latest?.version ?? null);
  // #33.3's payload is `{ version: … }` (T.Z142) — the row lives one level down.
  const baseVersion = base.data?.data?.version ?? null;
  const [prefilledFrom, setPrefilledFrom] = useState<number | null>(null);
  useEffect(() => {
    if (!baseVersion || prefilledFrom === baseVersion.version) return;
    setPrefilledFrom(baseVersion.version);
    // Only a PRISTINE draft is prefilled: anything the operator already typed
    // is his, and a late answer never stomps it.
    setDraft((prev) =>
      prev === EMPTY_DRAFT
        ? {
            key: '',
            title: '',
            channel: 'EMAIL',
            subject: baseVersion.subject,
            bodyHtml: baseVersion.bodyHtml,
            changelogNote: baseVersion.changelogNote ?? '',
          }
        : prev,
    );
  }, [baseVersion, prefilledFrom]);

  const busy = createTemplate.isPending || createVersion.isPending;
  const mutationError = createTemplate.error ? errorLineOf(createTemplate.error) : createVersion.error ? errorLineOf(createVersion.error) : null;
  const error = localError ?? mutationError;

  // T.Z210 — the draft's channel decides the editor's shape: SMS is PLAIN TEXT
  // (no HTML mode, no markup to break), e-mail keeps the hand-crafted HTML.
  const sms = isPlainTextChannel(draft.channel);
  const scanned = scanTemplateVariables(draft.subject, draft.bodyHtml);
  const preview = renderPreview(draft.subject, draft.bodyHtml, track);

  const submit = () => {
    setLocalError(null);
    if (isCreate) {
      const built = buildTemplateCreate(draft);
      if (!built.ok) {
        setLocalError(built.error);
        return;
      }
      createTemplate.mutate(built.value, {
        onSuccess: (answer) =>
          onSaved({ title: answer.data.template.title, version: answer.data.template.latestVersion ?? 1 }),
      });
      return;
    }
    const built = buildVersionCreate(draft);
    if (!built.ok) {
      setLocalError(built.error);
      return;
    }
    const templateId = template.id;
    const title = template.title;
    createVersion.mutate({ templateId, ...built.value }, {
      onSuccess: (answer) => onSaved({ title, version: answer.data.version.version }),
    });
  };

  return (
    <form
      className="mailings-template-editor"
      data-testid="mailings-template-editor"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <h3 className="mailings-template-editor-title" data-testid="mailings-template-editor-title">
        {isCreate ? 'Новый шаблон' : `Новая версия шаблона «${template.title}»`}
      </h3>
      <p className="mailings-template-editor-hint">
        {isCreate
          ? 'Ключ — служебное имя шаблона (латиница и дефисы), название видят составители списков. Версия 1 создаётся вместе с шаблоном.'
          : 'Сохранение создаёт НОВУЮ версию — существующие версии изменить нельзя, поэтому список, уже отправлявший письмо, читает ровно то, что отправлял.'}
      </p>

      {isCreate ? (
        <div className="mailings-template-editor-grid">
          <Field label="Канал" htmlFor="mailings-template-channel" required hint="Канал создаётся с шаблоном и не меняется: SMS-шаблон выбирают SMS-списки.">
            <Select
              id="mailings-template-channel"
              value={draft.channel}
              onChange={(event) => setDraft((prev) => ({ ...prev, channel: event.target.value as TemplateDraft['channel'] }))}
              data-testid="mailings-template-channel"
            >
              {CHANNEL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Ключ" htmlFor="mailings-template-key" required hint="латиница, цифры и дефисы, 2–64 знака">
            <Input
              id="mailings-template-key"
              value={draft.key}
              placeholder="documents-unsigned"
              onChange={(event) => setDraft((prev) => ({ ...prev, key: event.target.value }))}
              data-testid="mailings-template-key"
            />
          </Field>
          <Field label="Название" htmlFor="mailings-template-title" required>
            <Input
              id="mailings-template-title"
              value={draft.title}
              placeholder="Документы не подписаны"
              onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
              data-testid="mailings-template-title"
            />
          </Field>
        </div>
      ) : null}

      <Field
        label={sms ? 'Подпись (в сообщении не отправляется)' : 'Тема письма'}
        htmlFor="mailings-template-subject"
        required
        hint={sms ? 'SMS не несёт темы — поле служит служебной подписью шаблона в списках и версиях.' : undefined}
      >
        <Input
          id="mailings-template-subject"
          value={draft.subject}
          placeholder={sms ? 'Напоминание о документах' : `Документы не подписаны — ${SAMPLE_GREETING_NAME}`}
          onChange={(event) => setDraft((prev) => ({ ...prev, subject: event.target.value }))}
          data-testid="mailings-template-subject"
        />
      </Field>

      <Field
        label={sms ? 'Текст сообщения' : 'HTML письма'}
        htmlFor="mailings-template-body"
        required
        hint={
          sms
            ? 'Только текст, без HTML — уходит в SMS как есть. Переменные — {{name}} и {{start_url}}; одно сообщение — до нескольких SMS-частей, чем короче, тем дешевле.'
            : 'Письмо верстают руками: табличная вёрстка и инлайн-стили надёжнее внешних файлов стилей. Переменные — {{name}}, {{start_url}} и {{gosuslugi_test_link}} (последняя заполняется в письмах про согласие, про испытание и в рассылках списками с этой переменной).'
        }
      >
        <Textarea
          id="mailings-template-body"
          className="mailings-template-body"
          rows={sms ? 5 : 14}
          spellCheck={!sms}
          value={draft.bodyHtml}
          placeholder={
            sms
              ? '{{name}}, получите документы: {{start_url}}'
              : '<p>Здравствуйте, {{name}}!</p>\n<p><a href="{{start_url}}">Перейти к документам</a></p>'
          }
          onChange={(event) => setDraft((prev) => ({ ...prev, bodyHtml: event.target.value }))}
          data-testid="mailings-template-body"
        />
      </Field>

      <Field
        label="Пометка о версии"
        htmlFor="mailings-template-note"
        hint="Что изменилось и зачем — видно в списке версий и при выборе версии в списке рассылок."
      >
        <Input
          id="mailings-template-note"
          value={draft.changelogNote}
          placeholder="убрали напоминание о сроке"
          onChange={(event) => setDraft((prev) => ({ ...prev, changelogNote: event.target.value }))}
          data-testid="mailings-template-note"
        />
      </Field>

      <div className="mailings-template-vars" data-testid="mailings-template-vars">
        <span className="mailings-template-vars-label">Переменные письма:</span>
        {scanned.length === 0 ? (
          <span className="cell-muted" data-testid="mailings-template-vars-empty">
            нет — письмо уйдёт без подстановок
          </span>
        ) : (
          scanned.map((name) => (
            <code
              key={name}
              className={`mailings-template-var${isKnownVariable(name) ? ' mailings-template-var--known' : ''}`}
              title={
                isKnownVariable(name) ? describeVariable(name) : 'Эту переменную при отправке не заполнят — получатель увидит её как есть'
              }
              data-testid={`mailings-template-var-${name}`}
            >
              {'{{'}
              {name}
              {'}}'}
            </code>
          ))
        )}
      </div>

      <div className="mailings-template-preview" data-testid="mailings-template-preview">
        <div className="mailings-filters mailings-filters--plain">
          <h4>Предпросмотр с примерными данными</h4>
          <TrackToggle name="mailings-template-track" track={track} onTrack={setTrack} />
        </div>
        <p className="mailings-preview-meta" data-testid="mailings-template-preview-subject">
          Тема: {preview.subject || '—'}
        </p>
        {/* The same sandbox the letter previews use: no scripts, no same-origin. */}
        <iframe
          className="mailings-preview-frame mailings-template-preview-frame"
          title="Предпросмотр письма"
          sandbox=""
          srcDoc={preview.html}
          data-testid="mailings-template-preview-frame"
        />
      </div>

      {!isCreate && base.isError ? (
        <p className="mailings-template-editor-warn" data-testid="mailings-template-editor-warn">
          Не удалось прочитать последнюю версию — редактор открыт пустым. Сверьтесь с действующей версией, прежде чем
          сохранять правку.
        </p>
      ) : null}
      {!isCreate && base.isPending ? (
        <p className="cell-muted" data-testid="mailings-template-editor-loading">
          Читаем последнюю версию, чтобы начать правку с неё…
        </p>
      ) : null}

      {error ? (
        <p className="mailings-list-form-error" role="alert" data-testid="mailings-template-error">
          {error}
        </p>
      ) : null}

      <div className="mailings-list-form-actions">
        <Button variant="primary" type="submit" loading={busy} data-testid="mailings-template-submit">
          {isCreate ? 'Создать шаблон (версия 1)' : 'Сохранить как новую версию'}
        </Button>
        <Button onClick={onCancel} disabled={busy} data-testid="mailings-template-cancel">
          Отмена
        </Button>
        <span className="mailings-list-form-note">
          {isCreate
            ? 'Шаблон появится в выборе шаблонов для новых списков рассылок.'
            : 'Прежние версии останутся доступными — их можно открыть из списка версий.'}
        </span>
      </div>
    </form>
  );
}

/** The ИИ/Python radio pair that picks which welcome page the preview folds. */
function TrackToggle({
  name,
  track,
  onTrack,
}: {
  name: string;
  track: PreviewTrack;
  onTrack: (track: PreviewTrack) => void;
}) {
  return (
    <span className="mailings-template-track-toggle" role="radiogroup" aria-label="Трек для предпросмотра">
      <label className="mailings-list-form-mode">
        <input type="radio" name={name} checked={track === 'AI'} onChange={() => onTrack('AI')} data-testid={`${name}-ai`} />
        <span>Программа ИИ</span>
      </label>
      <label className="mailings-list-form-mode">
        <input
          type="radio"
          name={name}
          checked={track === 'PYTHON'}
          onChange={() => onTrack('PYTHON')}
          data-testid={`${name}-python`}
        />
        <span>Программа Python</span>
      </label>
    </span>
  );
}

// ------------------------------------------------------------------ the view --

/**
 * #33.3 — ONE version with its body, in TWO readings (T.Z184): the RENDERED
 * letter through the sample variables (the stored HTML carries `{{name}}`,
 * which reads as a broken letter) and the RAW SOURCE — the exact bytes the
 * version stores, untouched substitutions included, for an operator who needs
 * to READ what a version says without entering the editor. The markup itself
 * is edited in the editor; this modal never writes.
 */
function TemplateVersionModal({
  open,
  templateId,
  version,
  title,
  onClose,
}: {
  open: boolean;
  templateId: number | null;
  version: number | null;
  title: string;
  onClose: () => void;
}) {
  const [track, setTrack] = useState<PreviewTrack>('AI');
  /** `true` = the raw stored HTML instead of the rendered letter. */
  const [sourceMode, setSourceMode] = useState(false);
  // Another version in the same modal starts as the letter again — a stale
  // «HTML-код» switch must not survive the switch of the row it belonged to.
  useEffect(() => {
    if (open) setSourceMode(false);
  }, [open, templateId, version]);

  const read = useTemplateVersion(open ? templateId : null, open ? version : null);
  // #33.3's payload is `{ version: … }` (T.Z142) — reading the wrapper itself
  // handed `undefined` to the renderer and crashed the SPA on «Открыть».
  const row: EmailTemplateVersionFullDto | null = read.data?.data?.version ?? null;
  const preview = row ? renderPreview(row.subject, row.bodyHtml, track) : null;
  const chips = variableChips(row?.variables);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Версия ${version ?? ''} — ${title}`}
      description={row ? `Создана ${formatWhen(row.createdAt)}${row.changelogNote ? ` · ${row.changelogNote}` : ''}` : undefined}
      wide
    >
      {read.isError ? (
        <p className="mailings-template-editor-warn" data-testid="mailings-template-view-error">
          {errorLineOf(read.error)}
        </p>
      ) : read.isPending || !row || !preview ? (
        <Spinner label="Читаем версию" />
      ) : (
        <div className="mailings-template-view" data-testid="mailings-template-view">
          <p className="mailings-preview-meta" data-testid="mailings-template-view-subject">
            Тема: {preview.subject}
          </p>
          <div className="mailings-filters mailings-filters--plain mailings-template-view-bar">
            <div className="mailings-template-vars" data-testid="mailings-template-view-vars">
              <span className="mailings-template-vars-label">Переменные:</span>
              {chips.length === 0 ? (
                <span className="cell-muted">нет</span>
              ) : (
                chips.map((chip) => (
                  <code
                    key={chip.name}
                    className={`mailings-template-var${chip.known ? ' mailings-template-var--known' : ''}`}
                    title={chip.text}
                  >
                    {'{{'}
                    {chip.name}
                    {'}}'}
                  </code>
                ))
              )}
            </div>
            <span className="mailings-template-track-toggle" role="radiogroup" aria-label="Что показать">
              <label className="mailings-list-form-mode">
                <input
                  type="radio"
                  name="mailings-template-view-mode"
                  checked={!sourceMode}
                  onChange={() => setSourceMode(false)}
                  data-testid="mailings-template-view-mode-letter"
                />
                <span>Письмо</span>
              </label>
              <label className="mailings-list-form-mode">
                <input
                  type="radio"
                  name="mailings-template-view-mode"
                  checked={sourceMode}
                  onChange={() => setSourceMode(true)}
                  data-testid="mailings-template-view-mode-source"
                />
                <span>HTML-код</span>
              </label>
            </span>
            {!sourceMode ? <TrackToggle name="mailings-template-view-track" track={track} onTrack={setTrack} /> : null}
          </div>
          {sourceMode ? (
            <>
              <p className="mailings-preview-meta">
                Исходный HTML версии — ровно то, что хранится: подстановки не выполнены, переменные видны как есть.
              </p>
              {/* React escapes the text node: the markup is displayed, never run. */}
              <pre className="mailings-template-source" data-testid="mailings-template-view-source">
                <code>{row.bodyHtml}</code>
              </pre>
            </>
          ) : (
            <>
              {/* Third-party HTML: sandboxed with NO scripts and NO same-origin access. */}
              <iframe
                className="mailings-preview-frame mailings-template-preview-frame"
                title="Предпросмотр версии"
                sandbox=""
                srcDoc={preview.html}
                data-testid="mailings-template-view-frame"
              />
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

// --------------------------------------------------------------- the archive --

function ArchiveTemplateModal({
  template,
  onClose,
  onSettled,
}: {
  template: EmailTemplateDto | null;
  onClose: () => void;
  /** `errorLine` set = the write was refused; the line is honest, the api's words first. */
  onSettled: (archived: boolean, errorLine: string | null) => void;
}) {
  const archive = useArchiveTemplate();
  const archived = template !== null && isArchived(template);
  return (
    <Modal
      open={template !== null}
      onClose={onClose}
      title={archived ? 'Вернуть шаблон из архива?' : 'Убрать шаблон в архив?'}
      description={
        archived
          ? 'Шаблон снова можно выбирать в списках рассылок и дополнять версиями.'
          : 'Шаблон пропадёт из выбора шаблонов и закроется для новых версий. Созданные версии и списки, которые их закрепили, останутся как есть. Удалить шаблон нельзя.'
      }
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant={archived ? 'primary' : 'danger'}
            loading={archive.isPending}
            data-testid="mailings-template-archive-confirm"
            onClick={() => {
              if (template === null) return;
              const next = !archived;
              archive.mutate(
                { templateId: template.id, archived: next },
                {
                  onSuccess: () => onSettled(next, null),
                  // The api's own words first (409 TEMPLATE_ARCHIVED and friends);
                  // the flag is what we ASKED for and is only read when there is no error.
                  onError: (error) => onSettled(next, errorLineOf(error)),
                },
              );
            }}
          >
            {archived ? 'Вернуть' : 'В архив'}
          </Button>
        </>
      }
    >
      <p>
        Название: {template?.title ?? '—'} · ключ <code>{template?.key ?? '—'}</code> ·{' '}
        {template ? formatVersionsSummary(template) : '—'}
      </p>
    </Modal>
  );
}
